import * as Turbo from "@hotwired/turbo"
import { destroyStickyTableHeaders, initializeStickyTableHeaders } from "./sticky_table_headers.mjs"
const { MarkerClusterer } = require("@googlemaps/markerclusterer")

window.Turbo = Turbo
Turbo.config.drive.progressBarDelay = 150

require("local-time").start()
require("chartkick/chart.js")

function initializePingMaps() {
  if (!window.google?.maps) return

  document.querySelectorAll("[data-ping-map]:not([data-map-initialized])").forEach((element) => {
    element.dataset.mapInitialized = "true"
    const points = JSON.parse(element.dataset.points)
    const mapItem = element.dataset.mapItem || "ping"
    const popupLabel = element.dataset.mapPopupLabel
    const maxZoom = Number(element.dataset.mapMaxZoom || 16)
    const showSphereLink = element.dataset.showSphereLink !== "false"
    const showSessionLink = element.dataset.showSessionLink !== "false"
    const openFirstPopup = element.dataset.openFirstPopup === "true"
    const highlightNewest = element.dataset.highlightNewest === "true"
    const bounds = new google.maps.LatLngBounds()
    const map = new google.maps.Map(element, {
      disableDefaultUI: true,
      gestureHandling: "cooperative",
      mapTypeControl: false,
      zoomControl: true,
      styles: [{ featureType: "poi", stylers: [{ visibility: "off" }] }]
    })

    const infoWindow = new google.maps.InfoWindow({
      headerDisabled: true,
      maxWidth: 310,
      minWidth: 250
    })
    infoWindow.addListener("domready", () => {
      const popup = infoWindow.getContent()
      if (popup instanceof HTMLElement) popup.closest(".gm-style-iw-c")?.classList.add("ping-map-popup-shell")
    })

    const markerIcon = {
      path: google.maps.SymbolPath.CIRCLE,
      fillColor: "#ed1c24",
      fillOpacity: 1,
      scale: 6,
      strokeColor: "#ffffff",
      strokeWeight: 2
    }

    const NEWEST_PIN_SVG = encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="64" height="54" viewBox="0 0 64 54">
  <filter id="newest-shadow" x="-30%" y="-30%" width="160%" height="160%">
    <feDropShadow dx="0" dy="2.5" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.4"/>
  </filter>
  <g filter="url(#newest-shadow)">
    <rect x="7" y="1" width="50" height="17" rx="8.5" fill="#182026" stroke="#FFFFFF" stroke-width="1.5"/>
    <text x="32" y="12.5" fill="#FFFFFF" font-family="Inter, system-ui, -apple-system, sans-serif" font-size="9" font-weight="800" text-anchor="middle" letter-spacing="0.5">NEWEST</text>
    <path d="M32 18 C24 18 18 24 18 32 C18 41 29.5 50.5 31.4 52.2 C31.7 52.5 32.3 52.5 32.6 52.2 C34.5 50.5 46 41 46 32 C46 24 40 18 32 18 Z" fill="#ED1C24" stroke="#FFFFFF" stroke-width="2"/>
    <circle cx="32" cy="32" r="5.5" fill="#FFFFFF"/>
    <circle cx="32" cy="32" r="3" fill="#ED1C24"/>
  </g>
</svg>
`)

    const newestMarkerIcon = {
      url: `data:image/svg+xml;utf-8,${NEWEST_PIN_SVG}`,
      scaledSize: new google.maps.Size(64, 54),
      anchor: new google.maps.Point(32, 53)
    }

    function createInfoWindowContent(point, isNewest) {
      const content = document.createElement("div")
      content.className = "ping-map-popup relative min-w-[250px] max-w-[310px] bg-white p-3 font-sans text-sm text-[#182026]"

      const headerRow = document.createElement("div")
      headerRow.className = "flex items-center gap-2 pr-8"

      const status = document.createElement("span")
      status.className = isNewest
        ? "inline-flex items-center gap-1.5 rounded-full bg-[#FCE9EA] px-2.5 py-1 text-[11px] leading-none font-bold tracking-[0.04em] text-[#A3141A] uppercase"
        : "inline-flex items-center gap-1.5 text-[11px] leading-none font-bold tracking-[0.04em] text-[#4B5B64] uppercase"
      const dot = document.createElement("span")
      dot.className = "h-1.5 w-1.5 rounded-full bg-[#ED1C24]"
      status.appendChild(dot)
      status.appendChild(document.createTextNode(popupLabel || (isNewest ? "Newest ping" : "Recorded ping")))
      headerRow.appendChild(status)
      content.appendChild(headerRow)

      const closeButton = document.createElement("button")
      closeButton.type = "button"
      closeButton.className = "absolute top-2 right-2 grid h-7 w-7 place-items-center rounded-md text-[#66777F] transition-colors hover:bg-[#F1F3F4] hover:text-[#182026] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ED1C24]"
      closeButton.setAttribute("aria-label", "Close ping details")
      closeButton.innerHTML = '<svg aria-hidden="true" viewBox="0 0 20 20" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"><path d="M5 5l10 10M15 5L5 15"/></svg>'
      closeButton.addEventListener("click", () => infoWindow.close())
      content.appendChild(closeButton)

      const location = document.createElement("h3")
      location.className = "mt-2 pr-8 text-base leading-snug font-bold tracking-[-0.02em] text-[#182026]"
      location.textContent = point.location || "Location not named"
      content.appendChild(location)

      const details = document.createElement("div")
      details.className = "mt-1.5 text-xs leading-relaxed text-[#66777F]"
      const timeStr = point.time ? new Date(point.time).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : ""
      details.textContent = [timeStr, point.device].filter(Boolean).join(" · ")
      content.appendChild(details)

      const links = document.createElement("div")
      links.className = "mt-3 flex w-full flex-nowrap items-stretch gap-2 text-xs font-bold"

      if (showSphereLink && point.sphere_url && point.sphere) {
        const sphere = document.createElement("a")
        sphere.href = point.sphere_url
        sphere.className = "group inline-flex min-h-[42px] min-w-0 flex-1 items-center justify-center gap-2.5 rounded-lg bg-[#ED1C24] px-3 text-xs font-bold text-white transition-colors hover:bg-[#C9131A] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ED1C24]"
        sphere.setAttribute("aria-label", `View camera ${point.sphere}`)
        const sphereName = document.createElement("span")
        sphereName.className = "shrink-0 whitespace-nowrap"
        sphereName.textContent = point.sphere
        sphere.appendChild(sphereName)
        if (point.serial_number) {
          const serialBadge = document.createElement("span")
          serialBadge.className = "inline-flex shrink-0 items-center gap-1 border-l border-white/30 pl-2.5 font-mono text-[10px] font-semibold whitespace-nowrap"
          const serialLabel = document.createElement("span")
          serialLabel.className = "font-sans font-bold tracking-wide text-white/70 uppercase"
          serialLabel.textContent = "S/N"
          serialBadge.appendChild(serialLabel)
          serialBadge.appendChild(document.createTextNode(point.serial_number))
          sphere.appendChild(serialBadge)
        }
        const sphereCaret = document.createElement("span")
        sphereCaret.className = "ml-0.5 shrink-0 transition-transform group-hover:translate-x-0.5"
        sphereCaret.innerHTML = '<svg aria-hidden="true" viewBox="0 0 20 20" class="h-3.5 w-3.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m8 5 5 5-5 5"/></svg>'
        sphere.appendChild(sphereCaret)
        links.appendChild(sphere)
      }
      if (showSessionLink && point.session_url && point.session_number) {
        const session = document.createElement("a")
        session.href = point.session_url
        session.textContent = `Session #${point.session_number}`
        session.className = "inline-flex min-h-[42px] shrink-0 items-center justify-center rounded-lg bg-[#286271] px-3.5 text-xs font-bold text-white transition-colors hover:bg-[#1F4E5A] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#286271]"
        links.appendChild(session)
      }
      if (links.childElementCount > 0) content.appendChild(links)

      return content
    }

    let newestMarker = null
    let firstMarker = null
    let firstPoint = null
    const clusterMarkers = []

    points.forEach((point, index) => {
      const position = { lat: point.lat, lng: point.lng }
      bounds.extend(position)

      if (highlightNewest && index === 0) {
        // Standalone newest marker: NOT passed to MarkerClusterer so it never gets lost!
        newestMarker = new google.maps.Marker({
          position,
          map,
          title: `Newest ping: ${point.time}`,
          icon: newestMarkerIcon,
          zIndex: 2000000
        })

        newestMarker.addListener("click", () => {
          infoWindow.setContent(createInfoWindowContent(point, true))
          infoWindow.open({ map, anchor: newestMarker })
          highlightMatchingRow(point.id)
        })
        firstMarker ||= newestMarker
        firstPoint ||= point
      } else {
        const marker = new google.maps.Marker({
          position,
          title: `Ping: ${point.time}`,
          icon: markerIcon
        })

        marker.addListener("click", () => {
          infoWindow.setContent(createInfoWindowContent(point, false))
          infoWindow.open({ map, anchor: marker })
          highlightMatchingRow(point.id)
        })

        firstMarker ||= marker
        firstPoint ||= point
        clusterMarkers.push(marker)
      }
    })

    if (clusterMarkers.length > 0) {
      new MarkerClusterer({
        map,
        markers: clusterMarkers,
        renderer: {
          render: ({ count, position }) => new google.maps.Marker({
            position,
            title: `${count} ${count === 1 ? mapItem : `${mapItem}s`}`,
            zIndex: google.maps.Marker.MAX_ZINDEX + count,
            icon: {
              ...markerIcon,
              scale: Math.min(24, 16 + Math.log10(count) * 3),
              strokeWeight: 3
            },
            label: {
              text: String(count),
              color: "#ffffff",
              fontFamily: "Inter, system-ui, sans-serif",
              fontSize: count > 999 ? "10px" : "12px",
              fontWeight: "700"
            }
          })
        }
      })
    }

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds)
      google.maps.event.addListenerOnce(map, "idle", () => {
        if (map.getZoom() > maxZoom) map.setZoom(maxZoom)
        if (openFirstPopup && firstMarker && firstPoint) {
          infoWindow.setContent(createInfoWindowContent(firstPoint, highlightNewest))
          infoWindow.open({ map, anchor: firstMarker })
        }
      })
    }

    const hoverMarker = new google.maps.Marker({
      icon: markerIcon,
      zIndex: 3000000
    })

    const pingRows = document.querySelectorAll("[data-ping-row]")
    const pingList = document.querySelector("[data-ping-list]")
    const resetBtn = element.closest("section")?.querySelector("[data-map-reset-bounds]")
    let hoverTimer = null
    let activeRow = null

    function showResetButton() {
      if (resetBtn) resetBtn.classList.remove("hidden")
    }

    if (resetBtn) {
      resetBtn.addEventListener("click", () => {
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds)
          google.maps.event.addListenerOnce(map, "idle", () => {
            if (map.getZoom() > 16) map.setZoom(16)
          })
        }
        resetBtn.classList.add("hidden")
      })
    }

    function highlightMatchingRow(pingId) {
      if (!pingId) return
      const row = document.querySelector(`[data-ping-row][data-ping-id="${pingId}"]`)
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "nearest" })
        row.classList.add("ring-2", "ring-[#ED1C24]/50", "bg-[#FFF8F8]")
        setTimeout(() => {
          row.classList.remove("ring-2", "ring-[#ED1C24]/50", "bg-[#FFF8F8]")
        }, 2000)
      }
    }

    pingRows.forEach((row) => {
      const latStr = row.dataset.lat
      const lngStr = row.dataset.lng
      if (!latStr || !lngStr) return

      const lat = parseFloat(latStr)
      const lng = parseFloat(lngStr)
      if (isNaN(lat) || isNaN(lng)) return

      const position = { lat, lng }
      const isNewest = row.dataset.pingNewest === "true"
      const pingData = {
        lat,
        lng,
        time: row.dataset.time,
        location: row.dataset.location,
        device: row.dataset.device,
        sphere: row.dataset.sphere,
        serial_number: row.dataset.serialNumber,
        sphere_url: row.dataset.sphereUrl,
        session_url: row.dataset.sessionUrl,
        session_number: row.dataset.sessionNumber,
        map_url: row.dataset.mapUrl
      }

      const applyHover = () => {
        if (activeRow === row) return
        if (activeRow) {
          activeRow.classList.remove("bg-[#FFF5F5]", "border-l-[#ED1C24]")
        }
        activeRow = row
        row.classList.add("bg-[#FFF5F5]", "border-l-[#ED1C24]")

        const targetAnchor = isNewest && newestMarker ? newestMarker : hoverMarker
        if (!isNewest || !newestMarker) {
          hoverMarker.setPosition(position)
          hoverMarker.setMap(map)
        } else {
          hoverMarker.setMap(null)
        }

        infoWindow.setContent(createInfoWindowContent(pingData, isNewest))
        infoWindow.open({ map, anchor: targetAnchor })

        map.panTo(position)
        map.setZoom(15)
        showResetButton()
      }

      row.addEventListener("pointerenter", () => {
        clearTimeout(hoverTimer)
        hoverTimer = setTimeout(applyHover, 60)
      })
    })

    if (pingList) {
      pingList.addEventListener("pointerleave", () => {
        clearTimeout(hoverTimer)
        if (activeRow) {
          activeRow.classList.remove("bg-[#FFF5F5]", "border-l-[#ED1C24]")
          activeRow = null
        }
        hoverMarker.setMap(null)
        infoWindow.close()
      })
    }
  })
}

function initializePaginationLimitSelectors() {
  document.querySelectorAll("[data-pagination-limit]:not([data-pagination-limit-initialized])").forEach((select) => {
    select.dataset.paginationLimitInitialized = "true"
    select.addEventListener("change", () => Turbo.visit(select.value))
  })
}

function initializeFilterPopovers() {
  const popovers = document.querySelectorAll("[data-filter-popover]")

  popovers.forEach((popover) => {
    if (popover.dataset.popoverBound) return
    popover.dataset.popoverBound = "true"

    popover.addEventListener("toggle", () => {
      if (popover.open) {
        document.querySelectorAll("[data-filter-popover]").forEach((other) => {
          if (other !== popover) other.open = false
        })
      }
    })
    popover.querySelector("[data-clear-dates]")?.addEventListener("click", () => {
      popover.querySelectorAll('input[type="date"]').forEach((input) => { input.value = "" })
    })
    popover.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && popover.open) {
        popover.open = false
        popover.querySelector("summary")?.focus()
      }
    })
  })
}

// Global outside-click listener for filter popovers
document.addEventListener("click", (event) => {
  document.querySelectorAll("[data-filter-popover][open]").forEach((popover) => {
    if (!popover.contains(event.target)) popover.open = false
  })
})

function positionCheckboxPanel(filter) {
  const panel = filter.querySelector("[data-checkbox-filter-panel]")
  panel.style.left = ""
  if (filter.open && window.matchMedia("(min-width: 640px)").matches) {
    panel.style.left = `${Math.min(0, window.innerWidth - 16 - panel.getBoundingClientRect().right)}px`
  }
}

window.addEventListener("resize", () => {
  document.querySelectorAll("[data-checkbox-filter][open]").forEach(positionCheckboxPanel)
})

function initializeCheckboxFilters() {
  document.querySelectorAll("[data-checkbox-filter]").forEach((filter) => {
    if (filter.dataset.filterBound) return
    filter.dataset.filterBound = "true"

    const search = filter.querySelector("[data-checkbox-filter-search]")
    const options = [...filter.querySelectorAll("[data-checkbox-filter-option]")]
    const checkboxes = options.map((option) => option.querySelector('input[type="checkbox"]'))
    const label = filter.querySelector("[data-checkbox-filter-label]")
    const empty = filter.querySelector("[data-checkbox-filter-empty]")
    filter.addEventListener("toggle", () => positionCheckboxPanel(filter))

    const updateLabel = () => {
      const selected = checkboxes.filter((checkbox) => checkbox?.checked)
      label.textContent = selected.length === 0 ? " · All" : selected.length === 1 ? ` · ${selected[0].getAttribute("aria-label") || selected[0].value}` : ` · ${selected.length} selected`
    }

    search?.addEventListener("input", () => {
      const query = search.value.trim().toLowerCase()
      let visible = 0
      options.forEach((option) => {
        option.hidden = !option.textContent.toLowerCase().includes(query)
        if (!option.hidden) visible += 1
      })
      empty?.classList.toggle("hidden", visible !== 0)
    })

    checkboxes.forEach((checkbox) => checkbox?.addEventListener("change", updateLabel))
    filter.querySelector("[data-checkbox-filter-clear]")?.addEventListener("click", () => {
      checkboxes.forEach((checkbox) => { if (checkbox) checkbox.checked = false })
      updateLabel()
      search?.focus()
    })
    filter.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        filter.open = false
        filter.querySelector("summary")?.focus()
      }
    })
  })
}

function initializeLocationSphereLists() {
  document.querySelectorAll("[data-location-spheres-toggle]:not([data-location-spheres-bound])").forEach((button) => {
    button.dataset.locationSpheresBound = "true"
    const extraSpheres = document.getElementById(button.getAttribute("aria-controls"))
    const toggleLabel = button.querySelector("[data-location-spheres-toggle-label]")
    const caret = button.querySelector("[data-location-spheres-caret]")
    if (!extraSpheres) return

    button.addEventListener("click", () => {
      const expanded = button.getAttribute("aria-expanded") === "true"
      const nextExpanded = !expanded

      extraSpheres.hidden = !nextExpanded
      button.setAttribute("aria-expanded", String(nextExpanded))
      if (toggleLabel) toggleLabel.textContent = nextExpanded ? button.dataset.expandedLabel : button.dataset.collapsedLabel
      caret?.classList.toggle("rotate-180", nextExpanded)
      button.setAttribute("aria-label", nextExpanded ? button.dataset.expandedLabel : button.dataset.collapsedAriaLabel)
    })
  })
}

// Global delegated listener for linked rows with Turbo.visit
document.addEventListener("click", (event) => {
  const row = event.target.closest("[data-row-href]")
  if (!row) return
  if (event.target.closest("a, button, input, select, textarea, label")) return
  const href = row.dataset.rowHref
  if (href) {
    Turbo.visit(href)
  }
})

document.addEventListener("keydown", (event) => {
  if (event.key !== "Enter") return
  const row = event.target.closest("[data-row-href]")
  if (!row || event.target.closest("a, button, input, select, textarea, label")) return
  const href = row.dataset.rowHref
  if (href) {
    Turbo.visit(href)
  }
})

// Global delegated listeners for alerts and comment cancel
document.addEventListener("click", (event) => {
  const dismissAlert = event.target.closest("[data-dismiss-alert]")
  if (dismissAlert) {
    dismissAlert.closest("[role=alert]")?.remove()
    return
  }

  const cancelComment = event.target.closest("[data-cancel-comment]")
  if (cancelComment) {
    const details = cancelComment.closest("details")
    if (details) details.open = false
    return
  }

  const toggleSidebar = event.target.closest("[data-sidebar-toggle]")
  const sidebar = document.getElementById("app-sidebar")
  const backdrop = document.querySelector("[data-sidebar-backdrop]")
  if (toggleSidebar && sidebar && backdrop) {
    sidebar.classList.toggle("-translate-x-full")
    backdrop.classList.toggle("hidden")
    return
  }
  if (event.target === backdrop && sidebar && backdrop) {
    sidebar.classList.add("-translate-x-full")
    backdrop.classList.add("hidden")
  }
})

document.addEventListener("submit", (event) => {
  const form = event.target.closest("[data-comment-form]")
  if (form) {
    const submit = form.querySelector("[data-comment-submit]")
    if (submit) {
      submit.disabled = true
      submit.setAttribute("aria-busy", "true")
      submit.innerHTML = '<i class="fas fa-spinner fa-spin" aria-hidden="true"></i><span>Saving</span>'
    }
  }
})

function initializeCommentEditors() {
  document.querySelectorAll("[data-comment-editor]:not([data-editor-bound])").forEach((editor) => {
    editor.dataset.editorBound = "true"
    editor.addEventListener("toggle", () => {
      if (editor.open) editor.querySelector("[data-comment-input]")?.focus()
    })
  })
}

function closeMobileSidebar() {
  const sidebar = document.getElementById("app-sidebar")
  const backdrop = document.querySelector("[data-sidebar-backdrop]")
  if (sidebar && backdrop && window.innerWidth < 1024) {
    sidebar.classList.add("-translate-x-full")
    backdrop.classList.add("hidden")
  }
}

// ---------------------------------------------------------------------------
// Sidebar scrollbar & active indicator smooth vertical animation
// ---------------------------------------------------------------------------
let lastNavScrollTop = null
let lastActiveIndicatorTop = null

function captureSidebarState() {
  const nav = document.getElementById("sidebar-nav")
  if (!nav) return
  lastNavScrollTop = nav.scrollTop
  const activeLink = nav.querySelector('[aria-current="page"]')
  if (activeLink) {
    const indicator = activeLink.querySelector("[data-sidebar-indicator]")
    const target = indicator || activeLink
    lastActiveIndicatorTop = target.getBoundingClientRect().top
  } else {
    lastActiveIndicatorTop = null
  }
}

function animateSidebarNavigation() {
  const nav = document.getElementById("sidebar-nav")
  if (!nav) return

  // 1. Maintain sidebar scroll position across navigation first
  if (lastNavScrollTop !== null) {
    nav.scrollTop = lastNavScrollTop
  }

  const activeLink = nav.querySelector('[aria-current="page"]')
  if (!activeLink) return

  const indicator = activeLink.querySelector("[data-sidebar-indicator]")

  // 2. Animate active indicator gliding smoothly vertically from old to new position
  if (lastActiveIndicatorTop !== null && indicator) {
    const newTop = indicator.getBoundingClientRect().top
    const deltaY = lastActiveIndicatorTop - newTop

    if (Math.abs(deltaY) > 1) {
      indicator.animate(
        [
          { transform: `translateY(${deltaY}px)`, opacity: 0.85 },
          { transform: "translateY(0)", opacity: 1 }
        ],
        {
          duration: 320,
          easing: "cubic-bezier(0.2, 0.9, 0.3, 1)",
          fill: "both"
        }
      )
    }
  }

  // 3. Animate the scrollbar smoothly vertically so the active item is comfortably in view
  const navRect = nav.getBoundingClientRect()
  const activeRect = activeLink.getBoundingClientRect()
  const isAbove = activeRect.top < navRect.top + 24
  const isBelow = activeRect.bottom > navRect.bottom - 24

  if (isAbove || isBelow) {
    const relativeActiveTop = (activeRect.top - navRect.top) + nav.scrollTop
    const targetScroll = Math.max(0, relativeActiveTop - (nav.clientHeight / 2) + (activeRect.height / 2))

    nav.scrollTo({
      top: targetScroll,
      behavior: "smooth"
    })
    lastNavScrollTop = targetScroll
  } else {
    lastNavScrollTop = nav.scrollTop
  }

  if (indicator) {
    lastActiveIndicatorTop = indicator.getBoundingClientRect().top
  }
}

// ---------------------------------------------------------------------------
// Turbo lifecycle hooks
// ---------------------------------------------------------------------------
document.addEventListener("turbo:before-visit", () => {
  captureSidebarState()
})

document.addEventListener("turbo:before-render", () => {
  captureSidebarState()
})

document.addEventListener("turbo:render", () => {
  animateSidebarNavigation()
  closeMobileSidebar()
})

document.addEventListener("turbo:load", () => {
  document.querySelectorAll('[data-control-tabs]').forEach((tabs) => {
    const tab = tabs.querySelector('[aria-current="page"]')
    if (!tab) return
    const container = tabs.getBoundingClientRect()
    const selected = tab.getBoundingClientRect()
    if (selected.left < container.left || selected.right > container.right) {
      // Scroll only the tab strip; scrolling ancestors overrides log-line anchors.
      tabs.scrollLeft += selected.left - container.left - (container.width - selected.width) / 2
    }
  })
  initializePingMaps()
  initializePaginationLimitSelectors()
  initializeFilterPopovers()
  initializeCheckboxFilters()
  initializeLocationSphereLists()
  initializeCommentEditors()
  initializeStickyTableHeaders()
  animateSidebarNavigation()
})

document.addEventListener("turbo:before-cache", () => {
  destroyStickyTableHeaders()
  // Close any open popovers before caching
  document.querySelectorAll("[data-filter-popover][open]").forEach((popover) => {
    popover.open = false
  })
  // Reset maps so they reinitialize cleanly from cache
  document.querySelectorAll("[data-ping-map]").forEach((el) => {
    el.innerHTML = ""
    delete el.dataset.mapInitialized
  })
})
